import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiArrowLeft,
  FiUsers,
  FiCheckCircle,
  FiCalendar,
  FiClock,
  FiAward,
  FiFileText,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiActivity,
  FiCopy,
  FiCheck,
  FiGlobe,
  FiShield,
  FiZap,
  FiRotateCcw,
  FiExternalLink,
  FiUser,
  FiBell,
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  getEventById,
  getEventWinnersHistory,
  approveRewardStatus,
  revertRewardStatus,
} from "../api/eventApi";
import {
  formatUtcDateTime,
  formatLocalDateTime,
  calculateDuration,
} from "../utils/dateUtils";
import EventFormModal from "./components/events/EventFormModal";
import EventStatusModal from "./components/events/EventStatusModal";
import EventDeleteModal from "./components/events/EventDeleteModal";

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "running":
      return {
        label: "Running",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
      };
    case "upcoming":
      return {
        label: "Upcoming",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
      };
    case "completed":
      return {
        label: "Completed",
        className: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
      };
    case "noevent":
    default:
      return {
        label: "No Event",
        className: "bg-gray-100 text-gray-600 border-gray-200",
        dot: "bg-gray-400",
      };
  }
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedSlug, setCopiedSlug] = useState(false);

  // Modals for editing / status / delete / audit
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Winners History state for inline approval
  const [winnersList, setWinnersList] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchEventDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await getEventById(id);
      const data = res?.data || res;
      if (!data) {
        throw new Error("Event not found");
      }
      setEventData(data);
    } catch (err) {
      setError(err.message || "Failed to load event details");
      toast.error(err.message || "Failed to load event details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchWinners = useCallback(async () => {
    if (!id) return;
    setWinnersLoading(true);
    try {
      const res = await getEventWinnersHistory(id, { page: 1, limit: 15 });
      const data = res?.data || res;
      const list = data?.history || (Array.isArray(data) ? data : []);
      setWinnersList(list);
    } catch (err) {
      console.warn("Failed to load winner rewards preview", err);
    } finally {
      setWinnersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEventDetails();
    fetchWinners();
  }, [fetchEventDetails, fetchWinners]);

  const handleUpdateRewardStatus = async (historyId, newStatus) => {
    setActionLoadingId(historyId);
    try {
      if (newStatus === "credited") {
        await approveRewardStatus(historyId, "credited");
        toast.success("Reward approved & credited to user wallet!");
      } else {
        await revertRewardStatus(historyId);
        toast.success("Reward reverted back to pending.");
      }

      setWinnersList((prev) =>
        prev.map((item) =>
          item.historyId === historyId || item.id === historyId
            ? { ...item, rewardStatus: newStatus }
            : item
        )
      );
    } catch (err) {
      toast.error(err.message || "Failed to update reward status");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopySlug = () => {
    const slug = eventData?.eventId || id;
    if (!slug) return;
    navigator.clipboard.writeText(slug);
    setCopiedSlug(true);
    toast.success("Event ID copied to clipboard!");
    setTimeout(() => setCopiedSlug(false), 2000);
  };

  // Calculate event timeline progress
  let progressPercent = 0;
  let timeStatusLabel = "";
  if (eventData?.startDate && eventData?.endDate) {
    const start = new Date(eventData.startDate).getTime();
    const end = new Date(eventData.endDate).getTime();
    const now = Date.now();

    if (now < start) {
      progressPercent = 0;
      timeStatusLabel = "Upcoming";
    } else if (now > end) {
      progressPercent = 100;
      timeStatusLabel = "Ended";
    } else {
      progressPercent = Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
      timeStatusLabel = "Active Now";
    }
  }

  const statusInfo = getStatusBadge(eventData?.status);
  const prizesList = Array.isArray(eventData?.prizes) ? eventData.prizes : [];
  const termsList = Array.isArray(eventData?.termsAndConditions) ? eventData.termsAndConditions : [];

  const totalZx = prizesList.reduce((acc, curr) => acc + (Number(curr.rewardZx) || 0), 0);
  const coinTotals = prizesList.reduce((acc, curr) => {
    const hasCoin =
      curr.hasCoinReward != null
        ? Boolean(curr.hasCoinReward)
        : Boolean(
            curr.coinSymbol ||
              (curr.rewardUsdt && Number(curr.rewardUsdt) > 0) ||
              (curr.coinAmount && Number(curr.coinAmount) > 0)
          );
    const symbol = curr.coinSymbol || (curr.rewardUsdt ? "USDT" : null);
    const amount = Number(curr.coinAmount ?? curr.rewardUsdt ?? 0);
    if (hasCoin && symbol && amount > 0) {
      acc[symbol] = (acc[symbol] || 0) + amount;
    }
    return acc;
  }, {});

  const durationStr =
    eventData?.startDate && eventData?.endDate
      ? calculateDuration(
          eventData.startDate.slice(0, 16),
          eventData.endDate.slice(0, 16)
        )
      : null;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-4 sm:p-8 space-y-6">
          {/* Top Bar: Back button & Breadcrumbs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate("/events");
                  }
                }}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs flex items-center justify-center cursor-pointer"
                title="Back to Previous Page"
                aria-label="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
                  <span
                    onClick={() => navigate("/events")}
                    className="hover:text-black hover:underline cursor-pointer flex items-center gap-1"
                  >
                    Events
                  </span>
                  <span className="text-gray-400">/</span>
                  <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded truncate max-w-xs">
                    {eventData?.eventName || id}
                  </span>
                </div>
                <h2 className="text-2xl font-black text-gray-800 tracking-tight mt-0.5">
                  Event Details
                </h2>
              </div>
            </div>

            {/* Quick Actions Header */}
            {eventData && (
              <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                <button
                  onClick={fetchEventDetails}
                  disabled={loading}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs"
                  title="Refresh Details"
                >
                  <FiRefreshCw size={16} className={loading ? "animate-spin" : ""} />
                </button>
                <button
                  onClick={() =>
                    navigate(`/events/${eventData.eventId || eventData.id || id}/participants`)
                  }
                  className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiUsers size={14} /> Participants
                </button>
                <button
                  onClick={() =>
                    navigate(`/events/${eventData.eventId || eventData.id || id}/winners`)
                  }
                  className="flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiAward size={14} /> Winner Rewards
                </button>
                <button
                  onClick={() =>
                    navigate(`/events/${eventData.eventId || eventData.id || id}/notifications`)
                  }
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiBell size={14} /> Notifications
                </button>
                <button
                  onClick={() => setFormModalOpen(true)}
                  className="flex items-center gap-1.5 bg-white hover:bg-yellow-50 text-gray-800 border border-gray-200 hover:border-yellow-300 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiEdit2 size={13} className="text-amber-600" /> Edit Event
                </button>
                <button
                  onClick={() => setStatusModalOpen(true)}
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiActivity size={13} /> Change Status
                </button>
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs font-bold px-3 py-2.5 rounded-xl shadow-xs transition"
                >
                  <FiTrash2 size={13} /> Delete
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && !eventData ? (
            <div className="bg-white rounded-2xl p-16 border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-3 text-center">
              <div className="w-9 h-9 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-semibold text-gray-500">Loading event statistics &amp; details...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center gap-3 text-center">
              <p className="font-semibold text-base">{error}</p>
              <button
                onClick={() => navigate("/events")}
                className="px-4 py-2 bg-white border border-red-200 text-red-700 rounded-xl text-xs font-bold hover:bg-red-100 transition"
              >
                ← Return to Events List
              </button>
            </div>
          ) : eventData ? (
            <>
              {/* Event Hero Card */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start md:items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#ffffcc] text-[#999900] flex items-center justify-center font-bold text-2xl flex-shrink-0 shadow-xs">
                      🏆
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                          {eventData.eventName || "Untitled Event"}
                        </h1>
                        {eventData.status && (
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusInfo.className}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${statusInfo.dot} animate-pulse`} />
                            {statusInfo.label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2.5 py-1 rounded-md border border-gray-200 flex items-center gap-1.5">
                          <span>ID: {eventData.eventId || id}</span>
                          <button
                            onClick={handleCopySlug}
                            className="text-gray-400 hover:text-black transition"
                            title="Copy Slug"
                          >
                            {copiedSlug ? <FiCheck size={12} className="text-emerald-600" /> : <FiCopy size={12} />}
                          </button>
                        </span>
                        {eventData.id && (
                          <span className="text-xs text-gray-400 font-mono">
                            Database ID: #{eventData.id}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Summary Status Pill */}
                  <div className="flex items-center gap-2 bg-yellow-50/60 border border-yellow-200/80 px-4 py-2.5 rounded-xl self-start md:self-center text-xs">
                    <FiAward className="text-[#999900]" size={16} />
                    <span className="text-gray-600 font-medium">Prize Pool:</span>
                    <span className="font-bold text-gray-900">{totalZx.toLocaleString()} ZX</span>
                    {Object.entries(coinTotals).length > 0 && (
                      <span className="font-bold text-emerald-700">
                        + {Object.entries(coinTotals).map(([s, a]) => `${a.toLocaleString()} ${s}`).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Statistics Metric Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3.5">
                {/* 1. Participants */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-amber-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">
                        Participants
                      </span>
                      <FiUsers className="text-amber-500" size={16} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.participantCount ?? 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-amber-600 mt-0.5">Enrolled Users</p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/events/${eventData.eventId || eventData.id || id}/participants`)
                    }
                    className="mt-3 text-[11px] font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 py-1.5 px-2 rounded-lg transition text-center flex items-center justify-center gap-1"
                  >
                    <FiUsers size={12} /> View Users
                  </button>
                </div>

                {/* 2. Check-ins */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-emerald-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide">
                        Check-ins
                      </span>
                      <FiCheckCircle className="text-emerald-500" size={16} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.totalCheckins ?? 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-emerald-600 mt-0.5">Total Daily Tasks</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 italic">Daily verified</div>
                </div>

                {/* 3. Check-in Coin */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-yellow-300 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-[#999900] uppercase tracking-wide">
                        Check-in Coin
                      </span>
                      <span className="text-base">🪙</span>
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.checkInCoin ?? eventData.checkinCoin ?? eventData.checkInPoints ?? eventData.coin ?? 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-amber-600 mt-0.5">Coins / Check-in</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 font-mono">Daily reward</div>
                </div>

                {/* 4. Referral Coin */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-blue-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">
                        Referral Coin
                      </span>
                      <span className="text-base">👥</span>
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.referralCoin ?? eventData.referralCoins ?? eventData.referralPoints ?? eventData.referralBonus ?? 50).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-blue-600 mt-0.5">Coins / Referral</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 font-mono">Invite bonus</div>
                </div>

                {/* 5. Mining Coin */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-teal-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-teal-700 uppercase tracking-wide">
                        Mining Coin
                      </span>
                      <span className="text-base">⚡</span>
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.miningCycleCoin ?? eventData.miningCycleCoins ?? eventData.miningCycle ?? eventData.miningCyclePoints ?? eventData.miningCoin ?? 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-teal-600 mt-0.5">Coins / Cycle</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 font-mono">Mining cycle</div>
                </div>

                {/* 6. Browser Rate */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-indigo-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide">
                        Browser Rate
                      </span>
                      <span className="text-base">🌐</span>
                    </div>
                    <p className="text-2xl font-black text-gray-900">
                      {(eventData.browserPointsPerZx ?? eventData.browserPointsRate ?? 10).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-indigo-600 mt-0.5">Points / ZX Mined</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 font-mono">Zeta Browser</div>
                </div>

                {/* 7. Prize Pool */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-col justify-between hover:border-purple-200 transition">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wide">
                        Prize Pool
                      </span>
                      <FiAward className="text-purple-500" size={16} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-base font-black text-purple-700">
                        {totalZx.toLocaleString()} ZX
                      </span>
                      <span className="text-[11px] font-bold text-emerald-700 truncate">
                        {Object.entries(coinTotals).length > 0
                          ? Object.entries(coinTotals)
                              .map(([s, a]) => `${a.toLocaleString()} ${s}`)
                              .join(" + ")
                          : "ZX only"}
                      </span>
                    </div>
                    <p className="text-[10px] text-purple-500 mt-0.5">{prizesList.length} Tiers Configured</p>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 font-mono">Leaderboard</div>
                </div>
              </div>

              {/* Event Timeframe & Schedule Card */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center">
                      <FiCalendar size={16} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-800">Event Timeframe &amp; Schedule</h3>
                      <p className="text-xs text-gray-400">Universal Time (UTC) schedule</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {durationStr && (
                      <span className="text-xs font-semibold px-3 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200">
                        ⏱️ Duration: {durationStr}
                      </span>
                    )}
                    <span className="text-xs font-semibold px-3 py-1 rounded-lg bg-gray-100 text-gray-700 border border-gray-200">
                      Status: <strong className="text-gray-900">{timeStatusLabel}</strong> ({progressPercent}% elapsed)
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden p-0.5 border border-gray-200/60">
                  <div
                    className="bg-gradient-to-r from-[#ffff00] to-yellow-400 h-full rounded-full transition-all duration-500 shadow-xs"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  {/* Start Date */}
                  <div className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-3.5 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <FiClock className="text-emerald-600" size={14} />
                      <span>Start Date &amp; Time (UTC)</span>
                    </div>
                    <p className="text-sm font-mono font-bold text-gray-900 pl-5">
                      {formatUtcDateTime(eventData.startDate)}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 pl-5">
                      <FiGlobe size={11} className="text-gray-400" />
                      <span>Local Equivalent: {formatLocalDateTime(eventData.startDate)}</span>
                    </p>
                  </div>

                  {/* End Date */}
                  <div className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-3.5 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <FiClock className="text-red-500" size={14} />
                      <span>End Date &amp; Time (UTC)</span>
                    </div>
                    <p className="text-sm font-mono font-bold text-gray-900 pl-5">
                      {formatUtcDateTime(eventData.endDate)}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 pl-5">
                      <FiGlobe size={11} className="text-gray-400" />
                      <span>Local Equivalent: {formatLocalDateTime(eventData.endDate)}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Description (if present) */}
              {eventData.description && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-2">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <FiFileText size={14} /> Event Description
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed bg-gray-50/60 p-4 rounded-xl border border-gray-100">
                    {eventData.description}
                  </p>
                </div>
              )}

              {/* Prize Pool Distribution Table */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                      <FiAward size={16} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-800">
                        Prize Distribution Breakdown ({prizesList.length} Tiers)
                      </h3>
                      <p className="text-xs text-gray-400">Tokens &amp; coin allocations by rank</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFormModalOpen(true)}
                    className="text-xs text-[#999900] hover:underline font-semibold"
                  >
                    Edit Tiers
                  </button>
                </div>

                {prizesList.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                    No prize tiers configured for this event.
                  </div>
                ) : (
                  <div className="border border-gray-100 rounded-xl overflow-hidden shadow-xs">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3">Rank Tier</th>
                          <th className="px-5 py-3">ZX Reward</th>
                          <th className="px-5 py-3">Coin Reward</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {prizesList.map((tier, idx) => {
                          const isGold = tier.rankRange === "1";
                          const isSilver = tier.rankRange === "2" || tier.rankRange === "2-5";
                          const hasCoin =
                            tier.hasCoinReward != null
                              ? Boolean(tier.hasCoinReward)
                              : Boolean(
                                  tier.coinSymbol ||
                                    (tier.rewardUsdt && Number(tier.rewardUsdt) > 0) ||
                                    (tier.coinAmount && Number(tier.coinAmount) > 0)
                                );
                          const symbol = tier.coinSymbol || (tier.rewardUsdt ? "USDT" : null);
                          const amount = Number(tier.coinAmount ?? tier.rewardUsdt ?? 0);

                          return (
                            <tr key={idx} className="hover:bg-yellow-50/20 transition">
                              <td className="px-5 py-3.5 font-semibold text-gray-800 flex items-center gap-2">
                                <span className="text-base">
                                  {isGold ? "🥇" : isSilver ? "🥈" : "🎖️"}
                                </span>
                                <span className="font-bold text-gray-900">Rank {tier.rankRange}</span>
                              </td>
                              <td className="px-5 py-3.5 font-mono font-bold text-purple-700 text-sm">
                                {(Number(tier.rewardZx) || 0).toLocaleString()} ZX
                              </td>
                              <td className="px-5 py-3.5 font-mono font-medium">
                                {hasCoin && symbol && amount > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200 font-bold">
                                    🪙 + {amount.toLocaleString()} {symbol}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Screen 1: Event Winners & Rewards Approval Table */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center">
                      <FiAward size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-gray-800">
                          Leaderboard Winners &amp; Reward Approvals
                        </h3>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                          Wallet Credits
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">
                        Admin approval directly credits event prize coins into user wallets
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchWinners}
                      disabled={winnersLoading}
                      className="p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-gray-600 text-xs transition"
                      title="Refresh Winners"
                    >
                      <FiRefreshCw size={13} className={winnersLoading ? "animate-spin" : ""} />
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/events/${eventData.eventId || eventData.id || id}/winners`)
                      }
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-3 py-1.5 rounded-xl transition"
                    >
                      <span>Full Rewards View</span>
                      <FiExternalLink size={12} />
                    </button>
                  </div>
                </div>

                {winnersLoading && winnersList.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400 flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                    <span>Loading winner rewards...</span>
                  </div>
                ) : winnersList.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400 space-y-1">
                    <p className="font-semibold text-gray-600">No winner records found for this event yet.</p>
                    <p className="text-[11px]">Leaderboard records will populate as users earn event points.</p>
                  </div>
                ) : (
                  <div className="border border-gray-200/80 rounded-xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold tracking-wider border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 w-16">Rank</th>
                            <th className="px-4 py-3 min-w-[180px]">User</th>
                            <th className="px-4 py-3 text-center min-w-[120px]">Points / Score</th>
                            <th className="px-4 py-3 min-w-[140px]">Prize</th>
                            <th className="px-4 py-3 text-center whitespace-nowrap">Status</th>
                            <th className="px-4 py-3 text-center w-36 whitespace-nowrap">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {winnersList.map((row, idx) => {
                            const isPending =
                              row.rewardStatus === "pending" ||
                              !row.rewardStatus ||
                              row.rewardStatus === "PENDING";
                            const isCredited =
                              row.rewardStatus === "credited" || row.rewardStatus === "CREDITED";
                            const isRowBusy =
                              actionLoadingId === (row.historyId || row.id);
                            const zxVal = Number(row.rewardZx ?? row.zxAmount ?? 0);
                            const coinVal = Number(row.coinAmount ?? row.rewardUsdt ?? row.rewardCoin?.coin ?? 0);
                            const coinSymbol = row.coinSymbol || row.rewardCoin?.coinSymbol || "USDT";

                            return (
                              <tr key={row.historyId || idx} className="hover:bg-yellow-50/20 transition">
                                {/* Rank */}
                                <td className="px-4 py-3.5 font-bold text-gray-900 whitespace-nowrap">
                                  {Number(row.userRank ?? idx + 1) === 1 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                      🥇 #1
                                    </span>
                                  ) : Number(row.userRank ?? idx + 1) === 2 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-300">
                                      🥈 #2
                                    </span>
                                  ) : Number(row.userRank ?? idx + 1) === 3 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-950 border border-amber-200">
                                      🥉 #3
                                    </span>
                                  ) : (
                                    <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded text-[11px]">
                                      #{row.userRank ?? idx + 1}
                                    </span>
                                  )}
                                </td>

                                {/* User */}
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-yellow-100 text-black flex items-center justify-center font-bold text-[11px] flex-shrink-0">
                                      {row.username ? row.username.charAt(0).toUpperCase() : <FiUser />}
                                    </div>
                                    <div className="min-w-0">
                                      <span className="font-bold text-gray-900 block truncate">
                                        {row.username || "User"}
                                      </span>
                                      <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                        <span className="truncate max-w-[130px]">{row.email || "No email"}</span>
                                        <span className="font-mono bg-gray-100 text-gray-600 px-1 rounded">
                                          #{row.userId}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Points */}
                                <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                  <span className="font-mono font-bold text-gray-800 bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-lg">
                                    🪙 {(Number(row.points ?? row.score ?? row.checkInCoins ?? 0)).toLocaleString()}
                                  </span>
                                </td>

                                {/* Prize */}
                                <td className="px-4 py-3.5 whitespace-nowrap">
                                  <div className="flex flex-wrap items-center gap-1">
                                    {zxVal > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-mono font-bold text-[11px]">
                                        <FiZap size={11} className="text-purple-600" />
                                        {zxVal.toLocaleString()} ZX
                                      </span>
                                    )}
                                    {coinVal > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono font-bold text-[11px]">
                                        🪙 +{coinVal.toLocaleString()} {coinSymbol}
                                      </span>
                                    )}
                                    {zxVal === 0 && coinVal === 0 && (
                                      <span className="text-gray-400 text-xs">—</span>
                                    )}
                                  </div>
                                </td>

                                {/* Status */}
                                <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                  {isCredited ? (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                      CREDITED
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                      PENDING
                                    </span>
                                  )}
                                </td>

                                {/* Actions (Approve & Credit / Revert) */}
                                <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                  {isPending ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleUpdateRewardStatus(
                                          row.historyId || row.id,
                                          "credited"
                                        )
                                      }
                                      disabled={isRowBusy}
                                      className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] px-3 py-1.5 rounded-lg shadow-2xs transition disabled:opacity-50"
                                      title="Approve & Credit Reward to User Wallet"
                                    >
                                      {isRowBusy ? (
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <FiCheck size={12} />
                                      )}
                                      <span>Approve &amp; Credit</span>
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleUpdateRewardStatus(
                                          row.historyId || row.id,
                                          "pending"
                                        )
                                      }
                                      disabled={isRowBusy}
                                      className="inline-flex items-center gap-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-[11px] px-3 py-1.5 rounded-lg transition shadow-2xs disabled:opacity-50"
                                      title="Revert Credited Reward Back to Pending"
                                    >
                                      {isRowBusy ? (
                                        <div className="w-3 h-3 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <FiRotateCcw size={11} />
                                      )}
                                      <span>Revert Coins</span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FiFileText size={16} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">
                      Terms &amp; Conditions ({termsList.length})
                    </h3>
                    <p className="text-xs text-gray-400">Rules &amp; guidelines for participants</p>
                  </div>
                </div>

                {termsList.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                    No specific terms specified for this event.
                  </div>
                ) : (
                  <ul className="space-y-2.5">
                    {termsList.map((rule, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-xs text-gray-700 bg-gray-50/80 p-3.5 rounded-xl border border-gray-100 leading-relaxed"
                      >
                        <span className="font-bold text-[#999900] bg-[#ffffcc] w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs shadow-xs">
                          {idx + 1}
                        </span>
                        <span className="mt-0.5 text-gray-800 font-medium">{rule}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Timestamps Card */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-wrap items-center justify-between text-xs text-gray-400 gap-2">
                <span>Created: {formatUtcDateTime(eventData.createdAt)}</span>
                <span>Last Updated: {formatUtcDateTime(eventData.updatedAt)}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Modals for Editing, Changing Status, Deleting */}
      {formModalOpen && (
        <EventFormModal
          isOpen={formModalOpen}
          initialEvent={eventData}
          onClose={() => setFormModalOpen(false)}
          onSuccess={fetchEventDetails}
        />
      )}

      {statusModalOpen && (
        <EventStatusModal
          isOpen={statusModalOpen}
          event={eventData}
          onClose={() => setStatusModalOpen(false)}
          onSuccess={fetchEventDetails}
        />
      )}

      {deleteModalOpen && (
        <EventDeleteModal
          isOpen={deleteModalOpen}
          event={eventData}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={() => {
            toast.success("Event deleted");
            navigate("/events");
          }}
        />
      )}
    </div>
  );
};

export default EventDetail;
